import React from 'react';

/**
 * 개요 탭의 메모 캘린더 — 월별 달력과 날짜별 메모 입력.
 *
 * DashboardDetail 이 2,200줄이라 개요를 그 위에 더 쌓지 않고 떼어 냈다.
 * 메모 상태(memos, selectedDate, memo, memoCategory 등)는 "월별 메모 내역"
 * 테이블 모달과 공유돼 있어 DashboardDetail 이 그대로 들고 있고, 여기서는
 * props 로만 받는다 — 상태를 복제하면 저장 직후 달력에 반영되지 않는다.
 */
export default function MemoSection({
    currentDate,
    onPrevMonth,
    onNextMonth,
    memos,
    selectedDate,
    setSelectedDate,
    memo,
    setMemo,
    memoCategory,
    setMemoCategory,
    MEMO_CATEGORIES,
    isNewMemo,
    setIsNewMemo,
    showMemoModal,
    setShowMemoModal,
    handleSaveMemo,
    handleDeleteMemo,
    onOpenMemoTable,
}) {
    // 날짜 셀 클릭 핸들러 분기 추가
    const handleCalendarDayClick = (dateStr) => {
        const dayMemos = memos[dateStr]?.memos || [];
        if (dayMemos.length === 1) {
            setSelectedDate(dateStr);
            setMemo(dayMemos[0].content);
            setMemoCategory(dayMemos[0].category);
            setIsNewMemo(false);
            setShowMemoModal(true);
        } else if (dayMemos.length > 1) {
            onOpenMemoTable(dateStr);
        } else {
            setSelectedDate(dateStr);
            setMemo('');
            setMemoCategory('운동');
            setIsNewMemo(true);
            setShowMemoModal(true);
        }
    };

    // 달력 생성
    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const weeks = [];
        let days = [];

        // 이전 달의 날짜 채우기 (UI용, 날짜 매칭에는 영향 없음)
        for (let i = 0; i < firstDay.getDay(); i++) {
            const prevDate = new Date(year, month, -i);
            days.unshift(
                <td key={`prev-${i}`} className="calendar-day prev-month">
                    <span style={{ color: '#ccc', float: 'right' }}>{prevDate.getDate()}</span>
                </td>
            );
        }

        // 현재 달의 날짜 채우기
        for (let date = 1; date <= lastDay.getDate(); date++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
            // 날짜 매칭은 문자열로만 처리
            const hasMemo = memos[dateStr];
            const dayOfWeek = (firstDay.getDay() + date - 1) % 7;
            const isSunday = dayOfWeek === 0;
            const isSaturday = dayOfWeek === 6;
            let dayClass = 'calendar-day';
            if (isSunday) dayClass += ' sunday';
            if (isSaturday) dayClass += ' saturday';
            if (hasMemo?.hasData) dayClass += ' has-memo';
            days.push(
                <td
                    key={date}
                    className={dayClass}
                    style={{
                        padding: '10px',
                        border: '1px solid #ddd',
                        height: '120px',
                        width: '150px',
                        position: 'relative',
                        backgroundColor: 'white',
                        borderLeft: hasMemo?.hasData ? '4px solid #3498db' : '1px solid #ddd'
                    }}
                >
                    <div className="date-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="date-number" style={{ fontSize: '16px', fontWeight: 'bold' }}>{date}</span>
                        </div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            marginTop: '4px',
                            maxHeight: '80px',
                            overflowY: 'auto',
                            scrollbarWidth: 'thin'
                        }}>
                            {hasMemo?.memos?.map((memo, idx) => (
                                <div
                                    key={idx}
                                    className="memo-item"
                                    onClick={() => handleCalendarDayClick(dateStr)}
                                    style={{
                                        cursor: 'pointer',
                                        // backgroundColor, color 등은 CSS에서 처리
                                    }}
                                >
                                    {memo.content}
                                </div>
                            ))}
                            <button
                                className="add-memo-button"
                                onClick={() => {
                                    setSelectedDate(dateStr);
                                    setMemo('');
                                    setMemoCategory('운동');
                                    setIsNewMemo(true);
                                    setShowMemoModal(true);
                                }}
                                style={{
                                    cursor: 'pointer',
                                    // backgroundColor, color 등은 CSS에서 처리
                                    marginTop: 'auto',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                메모 {hasMemo?.hasData ? '추가' : '작성'}
                            </button>
                        </div>
                    </div>
                </td>
            );

            if ((firstDay.getDay() + date) % 7 === 0) {
                weeks.push(<tr key={date}>{days}</tr>);
                days = [];
            }
        }

        // 마지막 주 채우기 (UI용)
        if (days.length > 0) {
            const remainingDays = 7 - days.length;
            for (let i = 1; i <= remainingDays; i++) {
                days.push(
                    <td key={`next-${i}`} className="calendar-day next-month" style={{
                        width: '150px',
                        height: '120px',
                        padding: '10px'
                    }}>
                        <span style={{ color: '#ccc', float: 'right' }}>{i}</span>
                    </td>
                );
            }
            weeks.push(<tr key="last">{days}</tr>);
        }

        return weeks;
    };

    return (
            <div className="section calendar-section">
                <div className="calendar-section-header">
                    <h2>메모 캘린더</h2>
                </div>
                <div className="calendar-content">
                    <div className="calendar-header">
                        <button
                            className="calendar-nav-button"
                            onClick={onPrevMonth}
                        >
                            ◀
                        </button>
                        <h2>{currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월</h2>
                        <button
                            className="calendar-nav-button"
                            onClick={onNextMonth}
                        >
                            ▶
                        </button>
                    </div>
                    <table className="calendar">
                        <thead>
                            <tr>
                                <th className="sunday">일</th>
                                <th>월</th>
                                <th>화</th>
                                <th>수</th>
                                <th>목</th>
                                <th>금</th>
                                <th className="saturday">토</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderCalendar()}
                        </tbody>
                    </table>

                    {showMemoModal && (
                        <div className="memo-modal">
                            <div className="memo-modal-content">
                                <div className="memo-date-header">
                                    <h3>
                                        <span>{selectedDate.split('-')[0]}년 {selectedDate.split('-')[1]}월</span>
                                        <span>{selectedDate.split('-')[2]}</span>
                                        <span>일 </span>
                                    </h3>
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>카테고리</label>
                                    <select
                                        value={MEMO_CATEGORIES.includes(memoCategory) ? memoCategory : '선택'}
                                        onChange={e => setMemoCategory(e.target.value)}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    >
                                        <option value="선택">선택</option>
                                        {MEMO_CATEGORIES.map((category) => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                                <textarea
                                    className="memo-textarea"
                                    value={memo}
                                    onChange={e => setMemo(e.target.value)}
                                    placeholder="메모를 입력하세요"
                                    readOnly={!isNewMemo && memos[selectedDate]?.content}
                                />
                                <div className="memo-buttons">
                                    <button
                                        className="memo-close-button"
                                        onClick={() => setShowMemoModal(false)}
                                    >
                                        닫기
                                    </button>
                                    {isNewMemo ? (
                                        <button
                                            className="memo-save-button"
                                            onClick={handleSaveMemo}
                                        >
                                            저장
                                        </button>
                                    ) : (
                                        <button
                                            className="memo-delete-button"
                                            onClick={() => handleDeleteMemo(memos[selectedDate]?.memos[0].id)}
                                        >
                                            삭제
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
    );
}
